/**
 * Admin Tenant Management Service
 * API: /api/admin/tenants
 */

import client from '@lib/core/axios.client';
import { API_ENDPOINTS } from '@lib/constants';
import type {
  AdminTenantListParams,
  AdminTenantListResponse,
  TenantListItemDto,
  TenantDto,
  CreateTenantCommand,
  AdminUpdateTenantCommand,
  AdminTenantAiCreditStatusResponse,
} from '@/types/api/admin';

export class AdminTenantService {
  private static instance: AdminTenantService;

  private constructor() { }

  public static getInstance(): AdminTenantService {
    if (!AdminTenantService.instance) {
      AdminTenantService.instance = new AdminTenantService();
    }
    return AdminTenantService.instance;
  }

  /**
   * Get list of all tenants with pagination and filters
   */
  async getTenants(params?: AdminTenantListParams): Promise<AdminTenantListResponse> {
    return await client.get<AdminTenantListResponse>(
      API_ENDPOINTS.ADMIN.TENANTS.LIST,
      { params }
    );
  }

  /**
   * Get tenant by ID
   */
  async getTenantById(tenantId: string): Promise<TenantDto> {
    return await client.get<TenantDto>(
      API_ENDPOINTS.ADMIN.TENANTS.BY_ID(tenantId)
    );
  }

  /**
   * Create new tenant
   */
  async createTenant(data: CreateTenantCommand): Promise<TenantListItemDto> {
    return await client.post<TenantListItemDto>(
      API_ENDPOINTS.ADMIN.TENANTS.CREATE,
      data
    );
  }

  /**
   * Update existing tenant
   */
  async updateTenant(tenantId: string, data: AdminUpdateTenantCommand): Promise<TenantListItemDto> {
    return await client.put<TenantListItemDto>(
      API_ENDPOINTS.ADMIN.TENANTS.UPDATE(tenantId),
      data
    );
  }

  /**
   * Delete tenant
   */
  async deleteTenant(tenantId: string): Promise<void> {
    return await client.delete<void>(
      API_ENDPOINTS.ADMIN.TENANTS.DELETE(tenantId)
    );
  }

  /**
   * Get tenant WiseAI credit status
   */
  async getTenantAiCreditStatus(tenantId: string): Promise<AdminTenantAiCreditStatusResponse> {
    return await client.get<AdminTenantAiCreditStatusResponse>(
      API_ENDPOINTS.ADMIN.TENANTS.AI_CREDIT_STATUS(tenantId)
    );
  }

  /**
   * Update tenant data collector settings
   */
  async updateDataCollectorSettings(tenantId: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
    return await client.put<Record<string, unknown>>(
      API_ENDPOINTS.ADMIN.TENANTS.DATA_COLLECTOR_SETTINGS(tenantId),
      data
    );
  }
}

export const adminTenantService = AdminTenantService.getInstance();
export default adminTenantService;
